export type Operation = 'add' | 'subtract' | 'multiply' | 'divide';
export type InputMode = 'choices' | 'keypad';
export type Presentation = 'result' | 'missing';

export interface Settings {
  operations: Operation[];
  addLimit: 5 | 10 | 20;
  subtractLimit: 5 | 10 | 20;
  multiplicationTables: number[];
  divisionFamilies: number[];
  factorMax: 10 | 12;
  inputMode: InputMode;
  puzzles: boolean;
  soundPreferred: boolean;
  volume: number;
}

export interface Question {
  id: string;
  operation: Operation;
  a: number;
  b: number;
  answer: number;
  presentation: Presentation;
  equation: string;
  story: string;
  food: string;
  factKey: string;
}

export interface FactStats {
  firstCorrect: number;
  firstMisses: number;
  helpUses: number;
  independent: number;
  recent: boolean[];
}

export interface QuestionResult {
  questionId: string;
  factKey: string;
  operation: Operation;
  presentation: Presentation;
  inputMode: InputMode;
  firstCorrect: boolean;
  helped: boolean;
}

export interface RoundState {
  id: string;
  status: 'active' | 'complete';
  index: number;
  current: Question | null;
  currentInputMode: InputMode;
  currentFirstAttempted: boolean;
  currentFirstCorrect: boolean;
  currentHelped: boolean;
  wrongAttempts: number;
  results: QuestionResult[];
  operationPlan: Operation[];
  reviewQueue: Array<{ due: number; question: Question }>;
  seenQuestionIds: string[];
  selectedOperations: Operation[];
  rewardCommitted: boolean;
  tokensEarned?: number;
  newBadges: string[];
  newCatId?: string;
}

export interface PlayerTotals {
  independent: number;
  byOperation: Record<Operation, number>;
  rounds: number;
  distinctDoubles: string[];
  distinctTens: string[];
  distinctTwos: string[];
  distinctZeroSubtractions: string[];
  recoveredFacts: string[];
}

export interface Player {
  schemaVersion: 2;
  id: string;
  name: string;
  avatar: string;
  settings: Settings;
  factStats: Record<string, FactStats>;
  totals: PlayerTotals;
  badges: string[];
  cats: string[];
  tokens: number;
  cosmetics: string[];
  equipped: Record<string, string>;
  lastMenuFood?: string;
  round: RoundState | null;
  createdAt: string;
  updatedAt: string;
}

export const OP_INFO: Record<Operation, { symbol: string; word: string }> = {
  add: { symbol: '+', word: 'Addition' },
  subtract: { symbol: '−', word: 'Subtraction' },
  multiply: { symbol: '×', word: 'Multiplication' },
  divide: { symbol: '÷', word: 'Division' },
};

export const AVATARS = ['marmalade', 'tabby', 'gray-tabby', 'tuxedo', 'calico', 'cloud', 'cocoa', 'mint', 'sunbeam'];

export function defaultSettings(preset: 'little' | 'tables'): Settings {
  return preset === 'little'
    ? { operations: ['add'], addLimit: 10, subtractLimit: 10, multiplicationTables: [2], divisionFamilies: [2], factorMax: 10, inputMode: 'choices', puzzles: false, soundPreferred: false, volume: 0.55 }
    : { operations: ['multiply'], addLimit: 10, subtractLimit: 10, multiplicationTables: [2], divisionFamilies: [2], factorMax: 10, inputMode: 'choices', puzzles: false, soundPreferred: false, volume: 0.55 };
}

export function newPlayer(name: string, avatar: string, preset: 'little' | 'tables', id: string = crypto.randomUUID()): Player {
  const now = new Date().toISOString();
  return {
    schemaVersion: 2, id, name: name.trim().slice(0, 24) || 'Barista', avatar,
    settings: defaultSettings(preset), factStats: {}, badges: [], cats: [], tokens: 0, cosmetics: [], equipped: {}, round: null,
    totals: { independent: 0, byOperation: { add: 0, subtract: 0, multiply: 0, divide: 0 }, rounds: 0, distinctDoubles: [], distinctTens: [], distinctTwos: [], distinctZeroSubtractions: [], recoveredFacts: [] },
    createdAt: now, updatedAt: now,
  };
}

export function factStatKey(q: Question, mode: InputMode): string {
  return `${q.factKey}|${q.presentation}|${mode}`;
}

export function makeId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${crypto.randomUUID().slice(0, 8)}`;
}
